import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  UserCircle
} from 'lucide-react';

const AlertsManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterType, setFilterType] = useState('Todos');
  const [filterSeverity, setFilterSeverity] = useState('Todos');
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const alertTypes = [
    'Todos',
    'ausencias_repetidas',
    'sobrecarga',
    'riesgo_lesion',
    'sancion',
    'rendimiento_bajo',
    'otro'
  ];

  const severities = ['Todos', 'baja', 'media', 'alta'];

  useEffect(() => {
    fetchAlerts();
  }, [showActiveOnly, filterType, filterSeverity]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('player_alerts')
        .select(`
          *,
          players (
            id,
            first_name,
            last_name,
            team,
            photo_url,
            player_number
          )
        `)
        .order('created_at', { ascending: false });

      if (showActiveOnly) {
        query = query.eq('is_active', true);
      }

      if (filterType !== 'Todos') {
        query = query.eq('alert_type', filterType);
      }

      if (filterSeverity !== 'Todos') {
        query = query.eq('severity', filterSeverity);
      }

      const { data, error } = await query;

      if (error) throw error;

      setAlerts(data || []);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = async () => {
    try {
      setGenerating(true);

      // Llamar a la función SQL que genera alertas
      const { error } = await supabase.rpc('generate_all_alerts');

      if (error) throw error;

      // Recargar alertas
      await fetchAlerts();

      alert('Alertas generadas correctamente');
    } catch (error) {
      console.error('Error al generar alertas:', error);
      alert('Error al generar alertas: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const { error } = await supabase
        .from('player_alerts')
        .update({
          is_active: false,
          resolved_at: new Date().toISOString(),
          resolved_by: userProfile.id
        })
        .eq('id', alertId);

      if (error) throw error;

      // Actualizar estado local
      setAlerts(alerts.map(alert =>
        alert.id === alertId
          ? { ...alert, is_active: false }
          : alert
      ));
    } catch (error) {
      console.error('Error al resolver alerta:', error);
      alert('Error al resolver la alerta');
    }
  };

  const deleteAlert = async (alertId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta alerta?')) return;

    try {
      const { error } = await supabase
        .from('player_alerts')
        .delete()
        .eq('id', alertId);

      if (error) throw error;

      setAlerts(alerts.filter(alert => alert.id !== alertId));
    } catch (error) {
      console.error('Error al eliminar alerta:', error);
      alert('Error al eliminar la alerta');
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'alta': return 'bg-red-100 text-red-800 border-red-300';
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'baja': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      ausencias_repetidas: 'Ausencias Repetidas',
      sobrecarga: 'Sobrecarga Física',
      riesgo_lesion: 'Riesgo de Lesión',
      sancion: 'Sanción',
      rendimiento_bajo: 'Rendimiento Bajo',
      otro: 'Otro'
    };
    return labels[type] || type;
  };

  const getAlertIcon = (type) => {
    switch(type) {
      case 'ausencias_repetidas': return '📅';
      case 'sobrecarga': return '⚡';
      case 'riesgo_lesion': return '🏥';
      case 'sancion': return '🟨';
      case 'rendimiento_bajo': return '📉';
      default: return '⚠️';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando alertas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Alertas</h1>
                <p className="text-sm text-gray-600">{alerts.length} alertas encontradas</p>
              </div>
            </div>

            <button
              onClick={generateAlerts}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generando...' : 'Generar Alertas'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tipo de alerta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de alerta
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {alertTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'Todos' ? 'Todos' : getAlertTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Severidad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severidad
              </label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {severities.map(severity => (
                  <option key={severity} value={severity}>
                    {severity === 'Todos' ? 'Todas' : severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <div className="flex items-center gap-4 h-[42px]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={showActiveOnly}
                    onChange={() => setShowActiveOnly(true)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Solo activas</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!showActiveOnly}
                    onChange={() => setShowActiveOnly(false)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Todas</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de alertas */}
        {alerts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay alertas</h3>
            <p className="text-gray-600 mb-6">No se encontraron alertas con los filtros seleccionados</p>
            <button
              onClick={generateAlerts}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Generar Alertas
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                  !alert.is_active ? 'opacity-60' : ''
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Foto del jugador */}
                    {alert.players && (
                      <div className="flex-shrink-0">
                        {alert.players.photo_url ? (
                          <PrivateImage
                            bucket="player-photos"
                            path={alert.players.photo_url}
                            alt={`${alert.players.first_name} ${alert.players.last_name}`}
                            className="w-16 h-16 rounded-full object-cover"
                            fallbackIcon={UserCircle}
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                            <UserCircle className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Contenido de la alerta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{getAlertIcon(alert.alert_type)}</span>
                            <h3 className="text-lg font-bold text-gray-900">
                              {getAlertTypeLabel(alert.alert_type)}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                              {alert.severity.toUpperCase()}
                            </span>
                            {!alert.is_active && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                RESUELTA
                              </span>
                            )}
                          </div>
                          {alert.players && (
                            <p className="text-sm text-gray-600">
                              {alert.players.first_name} {alert.players.last_name} - {alert.players.team}
                            </p>
                          )}
                        </div>

                        {/* Acciones */}
                        {alert.is_active && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => resolveAlert(alert.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Resolver alerta"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => deleteAlert(alert.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar alerta"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </div>

                      <p className="text-gray-700 mb-3">{alert.description}</p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>
                          Creada: {new Date(alert.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        {alert.resolved_at && (
                          <span>
                            Resuelta: {new Date(alert.resolved_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        )}
                      </div>

                      {alert.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <strong>Notas:</strong> {alert.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsManagement;
